import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, BarChart3, Settings, LogOut, Link2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userInitials, setUserInitials] = useState('');
  const [pageTitle, setPageTitle] = useState('Dashboard');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    const getUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.user_metadata?.full_name) {
        const fullName = session.user.user_metadata.full_name;
        const initials = fullName
          .split(' ')
          .map(name => name[0])
          .join('')
          .toUpperCase();
        setUserInitials(initials);
      }
    };

    getUserData();
  }, []);

  useEffect(() => {
    // Update page title based on current route
    switch (location.pathname) {
      case '/dashboard':
        setPageTitle('Dashboard');
        break;
      case '/dashboard/integrations':
        setPageTitle('Integrations');
        break;
      case '/dashboard/analytics':
        setPageTitle('Analytics');
        break;
      case '/dashboard/settings':
        setPageTitle('Settings');
        break;
      default:
        setPageTitle('Dashboard');
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    
    try {
      setIsLoggingOut(true);

      // Clear all auth data regardless of session state
      await Promise.all([
        // Clear Supabase session
        supabase.auth.signOut({ scope: 'local' }),
        
        // Clear any stored tokens
        new Promise<void>(resolve => {
          localStorage.removeItem('sb-kobzjfyvaagljyvovaij-auth-token');
          localStorage.removeItem('supabase.auth.token');
          resolve();
        })
      ]);

      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Don't show error to user since we're redirecting anyway
    } finally {
      setIsLoggingOut(false);
      // Always redirect to home page
      navigate('/', { replace: true });
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#1E2048] bg-[#0A0B1A]/95">
        <div className="h-20 flex items-center px-6 border-b border-[#1E2048]">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <LayoutDashboard className="h-8 w-8 text-blue-400" />
            <span className="ml-2 text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-200 text-transparent bg-clip-text">
              UnifyAds
            </span>
          </Link>
        </div>
        <nav className="p-4">
          <ul className="space-y-2">
            <li>
              <Link
                to="/dashboard"
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard')
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-gray-400 hover:text-blue-400 hover:bg-blue-500/5'
                }`}
              >
                <LayoutDashboard className="h-5 w-5 mr-3" />
                Overview
              </Link>
            </li>
            <li>
              <Link
                to="/dashboard/analytics"
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard/analytics')
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-gray-400 hover:text-blue-400 hover:bg-blue-500/5'
                }`}
              >
                <BarChart3 className="h-5 w-5 mr-3" />
                Analytics
              </Link>
            </li>
            <li>
              <Link
                to="/dashboard/integrations"
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard/integrations')
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-gray-400 hover:text-blue-400 hover:bg-blue-500/5'
                }`}
              >
                <Link2 className="h-5 w-5 mr-3" />
                Integrations
              </Link>
            </li>
            <li>
              <Link
                to="/dashboard/settings"
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard/settings')
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-gray-400 hover:text-blue-400 hover:bg-blue-500/5'
                }`}
              >
                <Settings className="h-5 w-5 mr-3" />
                Settings
              </Link>
            </li>
          </ul>
        </nav>
        <div className="absolute bottom-0 w-64 p-4 border-t border-[#1E2048]">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex items-center w-full px-4 py-3 text-gray-400 hover:text-blue-400 hover:bg-blue-500/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogOut className="h-5 w-5 mr-3" />
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="h-20 border-b border-[#1E2048] bg-[#0A0B1A]/95 flex items-center justify-between px-8">
          <h1 className="text-xl font-semibold text-white">{pageTitle}</h1>
          <div className="flex items-center space-x-4">
            <button className="text-gray-400 hover:text-blue-400 transition-colors">
              <Settings className="h-5 w-5" />
            </button>
            <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 font-semibold">{userInitials || 'U'}</span>
            </div>
          </div>
        </div>
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;